import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { companyInfoAPI } from '../services/api';

interface CompanyInfoData {
  logo: {
    path: string;
    altText: string;
  };
  address: {
    street: string;
    suite: string;
    city: string;
    country: string;
  };
  phone: {
    main: string;
    support: string;
  };
  email: {
    general: string;
    sales: string;
    support: string;
  };
  businessHours: {
    weekdays: string;
    weekend: string;
  };
  socialMedia: {
    facebook: string;
    twitter: string;
    instagram: string;
    linkedin: string;
  };
  themeColor: string;
}

const CompanyInfo: React.FC = () => {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoData>({
    logo: { path: '', altText: 'Company Logo' },
    address: { street: '', suite: '', city: '', country: '' },
    phone: { main: '', support: '' },
    email: { general: '', sales: '', support: '' },
    businessHours: { weekdays: '', weekend: '' },
    socialMedia: { facebook: '', twitter: '', instagram: '', linkedin: '' },
    themeColor: 'yellow'
  });
  
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      const response = await companyInfoAPI.getCompanyInfo();
      setCompanyInfo(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch company information');
      console.error('Error fetching company info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (section: keyof CompanyInfoData, field: string, value: string) => {
    if (typeof companyInfo[section] === 'object' && companyInfo[section] !== null) {
      setCompanyInfo(prev => ({
        ...prev,
        [section]: {
          ...(prev[section] as object),
          [field]: value
        }
      }));
    } else {
      // For simple fields like themeColor
      setCompanyInfo(prev => ({
        ...prev,
        [section]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    setSuccess(null);
    setError(null);

    try {
      await companyInfoAPI.updateCompanyInfo(companyInfo);
      setSuccess('Company information updated successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update company information');
      console.error('Error updating company info:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    // Create FormData instance
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('altText', companyInfo.logo.altText);
    
    setUploading(true);
    setError(null);
    
    try {
      const response = await companyInfoAPI.uploadLogo(formData);
      
      // Update the company info with the new logo path
      setCompanyInfo(prev => ({
        ...prev,
        logo: response.data.logo
      }));
      
      setSuccess('Company logo uploaded successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload logo');
      console.error('Error uploading logo:', err);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="company-info-page">
      <h1 className="mb-4">Company Information</h1>
      <p className="text-muted mb-4">
        Update your company's contact information, business hours, and social media links. This information will be displayed on the website's contact page.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Company Logo</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6} className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0">
                {companyInfo.logo.path ? (
                  <div className="text-center">
                    <img 
                      src={`${companyInfo.logo.path}`} 
                      alt={companyInfo.logo.altText} 
                      style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                      className="mb-2 border p-2"
                    />
                    <p className="text-muted small">Current Logo</p>
                  </div>
                ) : (
                  <div className="text-center text-muted border p-5">
                    <i className="fas fa-image fa-4x mb-3"></i>
                    <p>No logo uploaded</p>
                  </div>
                )}
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Logo Alt Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.logo.altText}
                    onChange={(e) => handleChange('logo', 'altText', e.target.value)}
                    placeholder="Company Logo"
                  />
                  <Form.Text className="text-muted">
                    Text description of the logo for accessibility
                  </Form.Text>
                </Form.Group>
                <p className="mb-2">Upload New Logo:</p>
                <div className="d-grid">
                  <Button 
                    variant="outline-secondary"
                    onClick={() => document.getElementById('logo-upload')?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                        Uploading...
                      </>
                    ) : 'Select Image'}
                  </Button>
                  <Form.Control 
                    id="logo-upload"
                    type="file" 
                    accept="image/*"
                    className="d-none"
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        handleLogoUpload(file);
                      }
                    }}
                  />
                </div>
                <Form.Text className="text-muted d-block mt-2">
                  Recommended size: 200x80px. Max size: 1MB. Formats: JPG, PNG, SVG
                </Form.Text>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Address</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Street</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.address.street}
                    onChange={(e) => handleChange('address', 'street', e.target.value)}
                    placeholder="123 Security Avenue"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Suite/Floor</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.address.suite}
                    onChange={(e) => handleChange('address', 'suite', e.target.value)}
                    placeholder="Suite 500"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.address.city}
                    onChange={(e) => handleChange('address', 'city', e.target.value)}
                    placeholder="Riyadh"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.address.country}
                    onChange={(e) => handleChange('address', 'country', e.target.value)}
                    placeholder="Saudi Arabia"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Contact Information</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Main Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.phone.main}
                    onChange={(e) => handleChange('phone', 'main', e.target.value)}
                    placeholder="+966 558 128 XXX"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Support Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.phone.support}
                    onChange={(e) => handleChange('phone', 'support', e.target.value)}
                    placeholder="+966 558 128 XXX"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>General Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={companyInfo.email.general}
                    onChange={(e) => handleChange('email', 'general', e.target.value)}
                    placeholder="info@speedsafe.com"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sales Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={companyInfo.email.sales}
                    onChange={(e) => handleChange('email', 'sales', e.target.value)}
                    placeholder="sales@speedsafe.com"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Support Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={companyInfo.email.support}
                    onChange={(e) => handleChange('email', 'support', e.target.value)}
                    placeholder="support@speedsafe.com"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Business Hours</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Weekdays</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.businessHours.weekdays}
                    onChange={(e) => handleChange('businessHours', 'weekdays', e.target.value)}
                    placeholder="Sunday - Thursday: 9:00 AM - 6:00 PM"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Weekend</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.businessHours.weekend}
                    onChange={(e) => handleChange('businessHours', 'weekend', e.target.value)}
                    placeholder="Friday - Saturday: Closed"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Social Media</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Facebook</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.socialMedia.facebook}
                    onChange={(e) => handleChange('socialMedia', 'facebook', e.target.value)}
                    placeholder="https://facebook.com/speedsafe"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Twitter</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.socialMedia.twitter}
                    onChange={(e) => handleChange('socialMedia', 'twitter', e.target.value)}
                    placeholder="https://twitter.com/speedsafe"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Instagram</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.socialMedia.instagram}
                    onChange={(e) => handleChange('socialMedia', 'instagram', e.target.value)}
                    placeholder="https://instagram.com/speedsafe"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>LinkedIn</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyInfo.socialMedia.linkedin}
                    onChange={(e) => handleChange('socialMedia', 'linkedin', e.target.value)}
                    placeholder="https://linkedin.com/company/speedsafe"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Theme Color</h5>
          </Card.Header>
          <Card.Body>
            <Form.Select
              value={companyInfo.themeColor}
              onChange={(e) => handleChange('themeColor', '', e.target.value)}
            >
              <option value="red">Red</option>
              <option value="yellow">Yellow</option>
              <option value="orange">Orange</option>
              <option value="amber">Amber</option>
              <option value="lime">Lime</option>
              <option value="green">Green</option>
              <option value="emerald">Emerald</option>
              <option value="teal">Teal</option>
              <option value="cyan">Cyan</option>
              <option value="sky">Sky</option>
              <option value="blue">Blue</option>
              <option value="indigo">Indigo</option>
              <option value="violet">Violet</option>
              <option value="purple">Purple</option>
              <option value="fuchsia">Fuchsia</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Select a primary theme color for your website.
            </Form.Text>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-end mt-4">
          <Button 
            variant="primary" 
            type="submit" 
            disabled={updating}
            className="px-4"
          >
            {updating ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : 'Save Changes'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CompanyInfo; 